import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SiteService } from '../../../core/services/site.service';
import { DialogService } from '../../../core/services/dialog.service';
import { QrCodeDialogComponent } from '../qr-code-dialog/qr-code-dialog';

import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-site-manage',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatDialogModule,
    MatSnackBarModule
  ],
  template: `
    <div class="p-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">現場管理</h1>
        <a matButton="tonal" routerLink="/sites/create">
          <mat-icon>add</mat-icon>
          現場追加
        </a>
      </div>

      @if (siteService.isLoading()) {
        <div class="flex justify-center items-center py-12">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (siteService.error()) {
        <div class="bg-red-50 text-red-600 p-4 rounded-lg">
          {{ siteService.error() }}
        </div>
      } @else if (siteService.sites().length === 0) {
        <div class="bg-primary-container p-12 text-center rounded-lg shadow-sm">
          <mat-icon class="text-4xl text-gray-400 mb-2">domain_disabled</mat-icon>
          <p class="text-gray-500">現場が登録されていません</p>
        </div>
      } @else {
        <div class="bg-primary-container rounded-lg shadow-sm overflow-hidden">
          <table mat-table [dataSource]="siteService.siteListItems()" class="w-full">
            
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef> 現場名 </th>
              <td mat-cell *matCellDef="let site"> {{site.name}} </td>
            </ng-container>

            <ng-container matColumnDef="address">
              <th mat-header-cell *matHeaderCellDef> 住所 </th>
              <td mat-cell *matCellDef="let site"> {{site.address1}} {{site.address2 || ''}} </td>
            </ng-container>

            <ng-container matColumnDef="department">
              <th mat-header-cell *matHeaderCellDef> 管理部署 </th>
              <td mat-cell *matCellDef="let site"> {{ site.managementDepartmentName || '未設定' }} </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="w-16"></th>
              <td mat-cell *matCellDef="let site">
                <button matIconButton aria-label="現場操作" [matMenuTriggerFor]="menu">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" xPosition="before">
                  <a mat-menu-item [routerLink]="['/sites/drawings', site.id]">
                    <mat-icon>map</mat-icon>
                    <span>図面管理</span>
                  </a>
                  <a mat-menu-item [routerLink]="['/sites/sensors', site.id]">
                    <mat-icon>sensors</mat-icon>
                    <span>センサー管理</span>
                  </a>
                  <a mat-menu-item [routerLink]="['/sites/alerts', site.id]">
                    <mat-icon>notifications</mat-icon>
                    <span>アラート管理</span>
                  </a>
                  <button mat-menu-item (click)="openQrDialog(site.id, site.name)">
                    <mat-icon>qr_code_2</mat-icon>
                    <span>QR生成</span>
                  </button>
                  <a mat-menu-item [routerLink]="['/sites/edit', site.id]">
                    <mat-icon>edit</mat-icon>
                    <span>編集</span>
                  </a>
                  <button mat-menu-item (click)="deleteSite(site.id, site.name)">
                    <mat-icon class="text-red-600!">delete</mat-icon>
                    <span class="text-red-600!">削除</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50"></tr>
          </table>
        </div>
      }
    </div>
  `
})
export class SiteManageComponent {
  protected readonly siteService = inject(SiteService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);
  
  public displayedColumns: string[] = ['name', 'address', 'department', 'actions'];

  async openQrDialog(siteId: string, siteName: string): Promise<void> {
    try {
      const uuid = await this.siteService.getSiteUuid(siteId);
      if (!uuid) {
        this.snackBar.open('QRコード用UUIDが見つかりません', '閉じる', { duration: 3000 });
        return;
      }

      this.dialog.open(QrCodeDialogComponent, {
        width: '420px',
        data: {
          siteId,
          siteName,
          uuid
        }
      });
    } catch (error) {
      console.error('Failed to open QR dialog', error);
      this.snackBar.open('QRコード情報の取得に失敗しました', '閉じる', { duration: 3000 });
    }
  }

  async deleteSite(id: string, name: string): Promise<void> {
    const confirmed = await firstValueFrom(this.dialogService.confirm({
      title: '確認',
      message: `現場「${name}」を削除してもよろしいですか？`,
      confirmText: '削除',
      cancelText: 'キャンセル',
      color: 'warn'
    }));

    if (confirmed) {
      try {
        await this.siteService.deleteSite(id);
        this.snackBar.open('現場を削除しました', '閉じる', { duration: 3000 });
        this.siteService.reloadSites();
      } catch (error) {
        console.error('Failed to delete site', error);
        this.snackBar.open('現場の削除に失敗しました', '閉じる', { duration: 3000 });
      }
    }
  }
}
